/**
 * Webhosting-Sektion eines Mandanten (ADR 0015).
 * Eingehaengt in TenantDetailPage (Muster: VdiSection).
 *
 * Der Operator schaltet das Webhosting frei (AWX tenant-webhosting-provision:
 * Webspace-Export, MariaDB, SFTPGo, RoleBinding fuer den Controller,
 * Wildcard-DNS), setzt die Obergrenzen je Mandant und schaltet wieder ab.
 * Websites, Domains, Datenbanken und DNS-Zonen legt der Mandanten-Admin
 * danach selbst im Portal an; hier gibt es nur die Lesesicht und „Neu anwenden".
 * Phase 3: Traffic-Kontingent je Monat (notify | suspend) mit Anzeige des
 * laufenden Monats und „Sperre aufheben" fuer den aktuellen Monat.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  type WebhostingOverview, type WebhostingSiteRow, type WebhostingTrafficState,
  getTenantWebhosting, enableTenantWebhosting, updateTenantWebhosting, disableTenantWebhosting,
  resyncTenantWebhosting, listTenantWebhostingSites, getTenantWebhostingTraffic, liftTenantWebhostingTraffic, extractApiError,
} from '../services/api';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

const PHP_VERSIONS = ['8.2', '8.3', '8.4'];
// Node.js 20 ist abgekuendigt (EOL); bestehende Freigaben bildet das Backend auf 22 ab.
const NODE_VERSIONS = ['22', '24'];

const SUSPENDED_LABEL: Record<string, string> = { traffic: 'gesperrt (Traffic)', operator: 'gesperrt (Operator)' };

const STATUS_LABEL: Record<string, string> = {
  pending: 'wartet', provisioning: 'wird eingerichtet', active: 'aktiv', error: 'Fehler', deleting: 'wird gelöscht',
};

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KiB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GiB`;
}

export function WebhostingSection({ slug, isReadOnly }: Props) {
  const [ov, setOv] = useState<WebhostingOverview | null>(null);
  const [sites, setSites] = useState<WebhostingSiteRow[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [maxSites, setMaxSites] = useState('3');
  const [maxDomains, setMaxDomains] = useState('10');
  const [maxDatabases, setMaxDatabases] = useState('3');
  const [maxDnsZones, setMaxDnsZones] = useState('3');
  const [webspaceGi, setWebspaceGi] = useState('10');
  const [php, setPhp] = useState<Set<string>>(new Set(['8.3']));
  const [maxCronJobs, setMaxCronJobs] = useState('10');
  const [maxMailboxes, setMaxMailboxes] = useState('10');
  const [node, setNode] = useState<Set<string>>(new Set(['22']));
  const [trafficGi, setTrafficGi] = useState('100');
  const [trafficUnlimited, setTrafficUnlimited] = useState(false);
  const [trafficPolicy, setTrafficPolicy] = useState<'notify' | 'suspend'>('notify');
  const [traffic, setTraffic] = useState<WebhostingTrafficState | null>(null);

  function apply(o: WebhostingOverview): void {
    setOv(o);
    setMaxSites(String(o.config.maxSites));
    setMaxDomains(String(o.config.maxDomains));
    setMaxDatabases(String(o.config.maxDatabases));
    setMaxDnsZones(String(o.config.maxDnsZones));
    setWebspaceGi(String(o.config.webspaceGi));
    setPhp(new Set(o.config.phpVersions));
    setMaxCronJobs(String(o.config.maxCronJobs ?? 10));
    setMaxMailboxes(String(o.config.maxMailboxes ?? 10));
    setNode(new Set(o.config.nodeVersions ?? ['22']));
    setTrafficUnlimited(o.config.trafficGiPerMonth === null);
    setTrafficGi(String(o.config.trafficGiPerMonth ?? 100));
    setTrafficPolicy(o.config.trafficPolicy === 'suspend' ? 'suspend' : 'notify');
  }

  const load = useCallback(async () => {
    try {
      const o = await getTenantWebhosting(slug);
      apply(o);
      if (o.config.enabled) {
        setSites(await listTenantWebhostingSites(slug));
        setTraffic(await getTenantWebhostingTraffic(slug).catch(() => null));
      } else {
        setSites([]);
        setTraffic(null);
      }
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setLoaded(true);
    }
  }, [slug]);

  useEffect(() => { void load(); }, [load]);

  const ints = [maxSites, maxDomains, maxDatabases, maxDnsZones, webspaceGi, maxCronJobs, maxMailboxes].map(Number);
  const trafficOk = trafficUnlimited || (Number.isInteger(Number(trafficGi)) && Number(trafficGi) >= 1);
  const valid = ints.every((n) => Number.isInteger(n) && n >= 0) && Number(webspaceGi) >= 1 && php.size > 0 && node.size > 0 && trafficOk;
  const limits = () => ({
    maxSites: Number(maxSites), maxDomains: Number(maxDomains), maxDatabases: Number(maxDatabases),
    maxDnsZones: Number(maxDnsZones), webspaceGi: Number(webspaceGi), phpVersions: Array.from(php).sort(),
    maxCronJobs: Number(maxCronJobs), maxMailboxes: Number(maxMailboxes), nodeVersions: Array.from(node).sort(),
    trafficGiPerMonth: trafficUnlimited ? null : Number(trafficGi), trafficPolicy,
  });

  async function run(fn: () => Promise<string>): Promise<void> {
    if (isReadOnly) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      setMsg(await fn());
      await load();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const onEnable = () => {
    if (!valid) { setErr('Grenzen müssen ganze Zahlen ≥ 0 sein, Webspace und Traffic ≥ 1 GiB, mindestens eine PHP- und eine Node.js-Version.'); return; }
    void run(async () => `Freischaltung gestartet (AWX-Job #${(await enableTenantWebhosting(slug, limits())).jobId}).`);
  };
  const onUpdate = () => {
    if (!valid) { setErr('Grenzen müssen ganze Zahlen ≥ 0 sein, Webspace und Traffic ≥ 1 GiB, mindestens eine PHP- und eine Node.js-Version.'); return; }
    void run(async () => {
      const c = await updateTenantWebhosting(slug, limits());
      return c.lastJobId && Number(webspaceGi) !== ov?.config.webspaceGi
        ? `Grenzen übernommen; Webspace-Vergrößerung läuft (AWX-Job #${c.lastJobId}).`
        : 'Grenzen übernommen.';
    });
  };
  const onDisable = () => {
    if (!window.confirm('Webhosting für diesen Mandanten abschalten? Websites werden gestoppt; Dateien und Datenbanken bleiben erhalten.')) return;
    void run(async () => `Abschaltung gestartet (AWX-Job #${(await disableTenantWebhosting(slug, false)).jobId}).`);
  };
  const onPurge = () => {
    if (!window.confirm('Webhosting ENDGÜLTIG entfernen? Alle Websites, Dateien, Datenbanken und DNS-Zonen dieses Mandanten werden gelöscht.')) return;
    void run(async () => `Entfernen gestartet (AWX-Job #${(await disableTenantWebhosting(slug, true)).jobId}).`);
  };
  const onLift = () => {
    if (!window.confirm('Traffic-Sperre für den laufenden Monat aufheben? Die Websites werden sofort wieder erreichbar und bis zum Monatsende nicht erneut gesperrt.')) return;
    void run(async () => {
      const t = await liftTenantWebhostingTraffic(slug);
      return `Traffic-Sperre für ${t.month} aufgehoben.`;
    });
  };
  const onResync = () => {
    void run(async () => {
      const r = await resyncTenantWebhosting(slug);
      return `Neu angewendet: ${r.applied} Website(s), ${r.failed} fehlgeschlagen.`;
    });
  };

  if (!loaded) return <p className="text-slate-500 text-sm">Lade Webhosting…</p>;
  const cfg = ov?.config;
  const enabled = cfg?.enabled ?? false;

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">Webhosting</h3>
      <p className="text-xs text-slate-500 mb-3">
        Websites (PHP/Node.js/statisch) mit eigenem Webspace, Let's-Encrypt-Zertifikaten, MariaDB/Postgres-Datenbanken,
        DNS-Zonen auf den eigenen Nameservern (<span className="font-mono">{cfg?.nameservers.join(', ')}</span>),
        Dateizugang über <span className="font-mono">{cfg?.filesHost}</span>, E-Mail-Postfächer für Kundendomains,
        Cron-Jobs und Sicherungen je Website, WordPress-Installer, Git-Deploy, Weiterleitungen, Verzeichnisschutz sowie
        Zugriffsstatistik mit Traffic-Kontingent. Websites erreichen den Kunden unter
        <span className="font-mono"> &lt;name&gt;.{cfg?.siteDomainSuffix}</span> oder eigenen Domains.
        Anlegen und Verwalten übernimmt der Mandanten-Admin im Portal; hier werden Freischaltung und Obergrenzen gesetzt.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {msg && <div className="bg-green-100 text-green-700 p-3 rounded mb-3 text-sm">{msg}</div>}
      {!enabled && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          Webhosting ist für diesen Mandanten nicht freigeschaltet.
        </div>
      )}
      {ov && !ov.controllerConfigured && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          Der Webhosting-Dienst (webhosting-controller) ist auf dieser Instanz nicht konfiguriert — Websites können nicht angewendet werden.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-600">Obergrenzen</h4>
          {([
            ['Websites', maxSites, setMaxSites, ov?.usage.sites],
            ['Domains', maxDomains, setMaxDomains, ov?.usage.domains],
            ['Datenbanken', maxDatabases, setMaxDatabases, ov?.usage.databases],
            ['DNS-Zonen', maxDnsZones, setMaxDnsZones, ov?.usage.dnsZones],
            ['Cron-Jobs', maxCronJobs, setMaxCronJobs, ov?.usage.cronJobs],
            ['Postfächer', maxMailboxes, setMaxMailboxes, ov?.usage.mailboxes],
          ] as Array<[string, string, (v: string) => void, number | undefined]>).map(([label, val, set, used]) => (
            <div key={label}>
              <label className="block text-sm text-slate-600 mb-1">{label} <span className="text-slate-400">(belegt: {used ?? 0})</span></label>
              <input type="number" min={0} step="1" value={val} onChange={(e) => set(e.target.value)} disabled={isReadOnly}
                className="w-full px-2 py-1 rounded border font-mono text-sm" />
            </div>
          ))}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Webspace (GiB) <span className="text-slate-400">(belegt: {formatBytes(ov?.usage.storageBytes ?? null)})</span></label>
            <input type="number" min={1} step="1" value={webspaceGi} onChange={(e) => setWebspaceGi(e.target.value)} disabled={isReadOnly}
              className="w-full px-2 py-1 rounded border font-mono text-sm" />
            <p className="text-xs text-slate-400 mt-1">Kann nur vergrößert werden; eine Änderung startet das Provisionierungs-Playbook.</p>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Traffic je Monat (GiB) <span className="text-slate-400">(dieser Monat: {formatBytes(traffic?.usedBytes ?? ov?.usage.trafficBytesMonth ?? null)})</span></label>
            <div className="flex items-center gap-3">
              <input type="number" min={1} step="1" value={trafficGi} onChange={(e) => setTrafficGi(e.target.value)} disabled={isReadOnly || trafficUnlimited}
                className="w-full px-2 py-1 rounded border font-mono text-sm disabled:bg-slate-50" />
              <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                <input type="checkbox" checked={trafficUnlimited} disabled={isReadOnly} onChange={(e) => setTrafficUnlimited(e.target.checked)} />
                unbegrenzt
              </label>
            </div>
            <select value={trafficPolicy} onChange={(e) => setTrafficPolicy(e.target.value === 'suspend' ? 'suspend' : 'notify')} disabled={isReadOnly || trafficUnlimited}
              className="w-full mt-2 px-2 py-1 rounded border text-sm" aria-label="Verhalten bei Überschreitung">
              <option value="notify">Bei Überschreitung nur Hinweis (Portal + Audit)</option>
              <option value="suspend">Bei Überschreitung Websites bis Monatsende sperren</option>
            </select>
            <p className="text-xs text-slate-400 mt-1">Ein- und ausgehende Daten aller Websites aus den Traefik-Metriken, UTC-Kalendermonat.</p>
          </div>
          <div>
            <span className="block text-sm text-slate-600 mb-1">Erlaubte PHP-Versionen</span>
            <div className="flex gap-4">
              {PHP_VERSIONS.map((v) => (
                <label key={v} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={php.has(v)} disabled={isReadOnly}
                    onChange={(e) => setPhp((prev) => { const n = new Set(prev); if (e.target.checked) n.add(v); else n.delete(v); return n; })} />
                  PHP {v}
                </label>
              ))}
            </div>
          </div>
          <div>
            <span className="block text-sm text-slate-600 mb-1">Erlaubte Node.js-Versionen</span>
            <div className="flex gap-4">
              {NODE_VERSIONS.map((v) => (
                <label key={v} className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={node.has(v)} disabled={isReadOnly}
                    onChange={(e) => setNode((prev) => { const n = new Set(prev); if (e.target.checked) n.add(v); else n.delete(v); return n; })} />
                  Node {v}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {!enabled && (
              <button onClick={onEnable} disabled={busy || isReadOnly || !valid} className="btn-primary disabled:opacity-50">
                Webhosting freischalten
              </button>
            )}
            {enabled && (
              <>
                <button onClick={onUpdate} disabled={busy || isReadOnly || !valid} className="btn-primary disabled:opacity-50">Übernehmen</button>
                <button onClick={onResync} disabled={busy || isReadOnly} className="btn-secondary disabled:opacity-50">Websites neu anwenden</button>
                <button onClick={onDisable} disabled={busy || isReadOnly} className="btn-danger">Abschalten</button>
                <button onClick={onPurge} disabled={busy || isReadOnly} className="text-xs text-red-700 underline">endgültig entfernen</button>
              </>
            )}
          </div>
          {isReadOnly && <p className="text-xs text-slate-400">Read-only-Operator; Webhosting kann nicht bearbeitet werden.</p>}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-600">Status</h4>
          <div className="bg-white rounded shadow p-4 text-sm space-y-1">
            <div>Freigeschaltet: <span className="font-mono">{enabled ? 'ja' : 'nein'}</span></div>
            <div>Letzter AWX-Job: <span className="font-mono">{cfg?.lastJobId ?? '—'}</span></div>
            <div>Stack: <span className="font-mono">
              {ov?.stack ? (ov.stack.ready ? 'bereit' : 'nicht bereit') : (enabled ? 'unbekannt' : '—')}
            </span></div>
            {ov?.stack && (
              <div className="text-xs text-slate-500">
                Webspace {ov.stack.webspace.ready ? 'ok' : 'fehlt'} · MariaDB {ov.stack.mariadb.ready ? 'ok' : 'fehlt'} · Dateizugang {ov.stack.sftpgo.ready ? 'ok' : 'fehlt'}
              </div>
            )}
            <div>Dateikonten: <span className="font-mono">{ov?.usage.fileAccounts ?? 0}</span></div>
            <div>Anwendungen: <span className="font-mono">{ov?.usage.apps ?? 0}</span></div>
          </div>

          {traffic && (
            <>
              <h4 className="text-sm font-semibold text-slate-600 pt-2">Traffic {traffic.month}</h4>
              <div className={`rounded shadow p-4 text-sm space-y-1 ${traffic.exceeded && !traffic.overridden ? 'bg-red-50' : 'bg-white'}`}>
                <div>
                  <span className="font-mono">{formatBytes(traffic.usedBytes)}</span>
                  {' '}{traffic.limitBytes === null ? '(unbegrenzt)' : <>von <span className="font-mono">{formatBytes(traffic.limitBytes)}</span></>}
                </div>
                {traffic.exceeded && (
                  <div className="text-red-700">
                    Kontingent überschritten{traffic.policy === 'suspend' ? ` · ${traffic.suspendedSites} Website(s) gesperrt` : ' · nur Hinweis (Portal + Audit)'}
                    {traffic.overridden && <span className="text-slate-600"> · Sperre für diesen Monat aufgehoben</span>}
                  </div>
                )}
                {traffic.exceeded && traffic.policy === 'suspend' && !traffic.overridden && (
                  <button onClick={onLift} disabled={busy || isReadOnly} className="btn-secondary disabled:opacity-50 mt-1">Sperre aufheben</button>
                )}
              </div>
            </>
          )}

          <h4 className="text-sm font-semibold text-slate-600 pt-2">Websites</h4>
          <div className="bg-white rounded shadow text-sm">
            {(sites ?? []).length === 0 && <p className="p-3 text-slate-400">Keine Websites.</p>}
            {(sites ?? []).map((s) => (
              <div key={s.id} className="border-t first:border-t-0 p-3 flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{s.name} <span className="text-xs text-slate-400 font-mono">{s.runtime}{s.phpVersion ? ` ${s.phpVersion}` : ''}</span></div>
                  <div className="text-xs text-slate-500 font-mono">{s.domains.map((d) => d.fqdn).join(', ')}</div>
                  {s.statusMessage && <div className="text-xs text-red-700">{s.statusMessage}</div>}
                  {s.suspendedReason && <div className="text-xs text-red-700">{SUSPENDED_LABEL[s.suspendedReason] ?? 'gesperrt'}</div>}
                </div>
                <span className={`badge ${s.status === 'active' ? 'bg-green-100 text-green-800' : s.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                  {STATUS_LABEL[s.status] ?? s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
